import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import parse, { domToReact } from 'html-react-parser';
import React from "react"
import reactStringReplace from 'react-string-replace';

import Fluid from 'components/Fluid';
import Image from 'components/Image';
import Link from 'components/Link';
import Spacer from 'components/Spacer';
import { ReactComponent as ArrowUpRight } from 'images/icons/arrow_up_right.svg';
import AccordionListProvider from 'providers/AccordionListProvider';
import CallOutProvider from "providers/CallOutProvider";
import CallToActionProvider from "providers/CallToActionProvider";
import ComparisonCardBlockProvider from 'providers/ComparisonCardBlockProvider';
import ComparisonCardsContentLinkProvider from 'providers/ComparisonCardsContentLinkProvider';
import CrowdRiffProvider from 'providers/CrowdRiffProvider';
import CuratedListProvider from 'providers/CuratedListProvider';
import CustomCardSliderProvider from 'providers/CustomCardSliderProvider';
import ImageSliderProvider from "providers/ImageSliderProvider";
import ListingSliderProvider from 'providers/ListingSliderProvider';
import MultiCTACardProvider from 'providers/MultiCTACardProvider';
import NeighborhoodsMapProvider from 'providers/NeighborhoodsMapProvider';
import SimpleviewFormProvider from 'providers/SimpleviewFormProvider';
import TextSliderProvider from 'providers/TextSliderProvider';
import Threshold360Provider from 'providers/ThresholdProvider';
import VerticalTextThreeSlideProvider from "providers/VerticalTextThreeSlideProvider";
import FlagChart from 'shortcodes/FlagChart';
import FlagCondition from 'shortcodes/FlagCondition';

const smartTags = {
	ImageSlider: ImageSliderProvider,
	CallToAction: CallToActionProvider,
	VerticalTextThreeSlides: VerticalTextThreeSlideProvider,
	NeighborhoodsMap: NeighborhoodsMapProvider,
	ComparisonCardsContentLink: ComparisonCardsContentLinkProvider,
	TextSlider: TextSliderProvider,
	MultiCTACard: MultiCTACardProvider,
	ComparisonCardBlock: ComparisonCardBlockProvider,
	CuratedList: CuratedListProvider,
	SimpleviewForm: SimpleviewFormProvider,
	CrowdRiff: CrowdRiffProvider,
	AccordionList: AccordionListProvider,
	ListingSlider: ListingSliderProvider,
	Threshold360: Threshold360Provider,
	CustomCardSlider: CustomCardSliderProvider,
	CallOut: CallOutProvider,
}

const formatStringToCamelCase = str => {
	const splitted = str.split("-");
	const filteredSplitted = splitted.filter(item => item !== "");
	if (filteredSplitted.length === 1) return filteredSplitted[0];
	return filteredSplitted[0] + filteredSplitted.slice(1).map(word => word[0].toUpperCase() + word.slice(1)).join("")
};

export const getStyleObjectFromString = str => {
	const style = {};
	str.split(";").forEach(el => {
		const [property, value] = el.split(":");
		if (!property) return;
		const formattedProperty = formatStringToCamelCase(property.trim());
		style[formattedProperty] = value.trim();
	});
	return style;
};

const shortCodes = {
	flag_condition: FlagCondition,
	flag_chart: FlagChart,
}

const onlyContains = ({children, tags = []}) => {
	const shortCodeRE = /(?<shortcode>\[\w+\])/i;
	let valid = true;
	if (children === null)return true;
	for (const child of children) {
		if (child.type === 'tag' && !tags.includes(child.name))valid = false;
		if (child.type === 'text' && shortCodeRE.test(child.data))valid = false;
		if (valid === false)return valid;
	}
	if (children.children) {
		valid = onlyContains(children.children, tags);
	}
	return valid;
}

const parserOptions = {
	replace: ({ name, type, attribs, data, children, ...node }) => {
		// const shortCodeRE = /(?<fullmatch>\[(?<shortcode>\w+)(.*?)?\](?:(.+?)?\[\/\1\]))?/ig;
		const shortCodeRE = /(?<shortcode>\[\w+\])/ig;
		const phraseTags = ['span', 'a', 'strong', 'em', 'u'];
		if (attribs === undefined) attribs = {};
		if (attribs.style !== undefined && typeof attribs.style === 'string') attribs.style = getStyleObjectFromString(attribs.style);
		if (attribs.class && !attribs.className) {
			attribs.className = attribs.class;
		}
		delete attribs.class;
		switch (name) {
			case "a":
				let Component;
				if (attribs.className?.includes('boldArrow')) {
					attribs.variant = 'bold_arrow';
				}
				if (attribs.className?.includes('button')) {
					Component = Button;
				} else Component = Link;
				return <Component {...attribs}>{domToReact(children, parserOptions)}{attribs.variant === 'bold_arrow' && <ArrowUpRight />}</Component>

			case "img":
				let isCentered = false;
				if (attribs.caption === "false") delete attribs.caption;
				if (attribs.hasOwnProperty('style')) {
					if (attribs.style.display === 'block' && attribs.style.marginLeft === 'auto' && attribs.style.marginRight === 'auto') {
						isCentered = true;
					}
				}
				return (
					<Image {...attribs} alt={attribs.alt} className="content__img" isCentered={isCentered} />
				)

			case 'div':
			case 'ul':
			case 'li':
			case 'p':
				let TagName = name;
				if (name === 'p' && !onlyContains({children, tags: phraseTags})) {
					TagName = 'div';
					attribs.className = (attribs.className ? attribs.className + ' ' : '') + 'content__mimic';
				}
				return (
					<TagName {...attribs}>
						{domToReact(children, parserOptions)}
					</TagName>
				)

			case 'h1':
			case 'h2':
			case 'h3':
			case 'h4':
			case 'h5':
			case 'h6':
				return <Typography variant={name} {...attribs}>{domToReact(children, parserOptions)}</Typography>

			case 'z-grid':
				delete attribs.contenteditable;
				delete attribs['data-editable-grid'];
				let zGridClasses = ['content__fluid--smarttag'];
				if (attribs.layout === undefined) attribs.layout = "12";
				if (attribs.columngap) attribs.gap = { sm: undefined, md: attribs.columngap };
				if (attribs.justifycontent) attribs.justifyContent = attribs.justifycontent; delete attribs.justifycontent;
				if (attribs.bgcolor) zGridClasses = [...zGridClasses, attribs.bgcolor]; delete attribs.bgcolor;
				if (attribs.spacing) zGridClasses = [...zGridClasses, attribs.spacing]; delete attribs.spacing;
				if (attribs.style !== undefined && typeof attribs.style === 'object' && attribs.style.hasOwnProperty('bghex')) {
					delete attribs.style.bghex;
				}
				if (attribs.style !== undefined && typeof attribs.style === 'string') attribs.style = getStyleObjectFromString(attribs.style);
				return (
					<Fluid className={zGridClasses.join(' ')} {...attribs}>
						{domToReact(children, parserOptions)}
					</Fluid>
				)

			case 'smarttag':
				if (!!attribs.component === false) return null;
				const { component } = attribs;
				delete attribs.component;
				for (const key in attribs) {
					if (attribs[key] === 'false') attribs[key] = false;
					if (attribs[key] === 'true') attribs[key] = true;
				}
				if (smartTags[component] !== undefined) {
					const SmartTag = smartTags[component];
					return (
						<>
							<SmartTag {...attribs} />
							{domToReact(children, parserOptions)}
						</>
					)
				} else {
					// Manually do it
					return <>{component} is not a valid smart tag.</>;
				}

			case 'spacer':
				delete attribs.component;
				delete attribs.contenteditable;
				return <Spacer {...attribs} />

			case 'table':
				return (
					<TableContainer className={'content__table'} component={Paper}>
						<Table>{domToReact(children, parserOptions)}</Table>
					</TableContainer>
				)

			case 'thead':
				return (
					<TableHead>{domToReact(children, parserOptions)}</TableHead>
				)

			case 'tbody':
				return (
					<TableBody>{domToReact(children, parserOptions)}</TableBody>
				)

			case 'tr':
				return (
					<TableRow>{domToReact(children, parserOptions)}</TableRow>
				)

			case 'th':
			case 'td':
				return (
					<TableCell>{domToReact(children, parserOptions)}</TableCell>
				)

			default: {
				if (type === 'text' && new RegExp(shortCodeRE.source).test(data)) {
					data = reactStringReplace(data, shortCodeRE, (match, i) => {
						const clean = match.replace(/\[|\]/g, '');
						const ShortCode = shortCodes[clean];
						if (!ShortCode)return match;
						return <React.Fragment key={i}><ShortCode input={data} /></React.Fragment>
					});
					return (
						<>{data}</>
					)
				}
			}
		}
	},
};

export default function zparse(str, options = parserOptions) {
	if (!str) return "";
	if (typeof str !== 'string') return str;
	return parse(str, options);
}